// src/components/GameTilePicker.js
// ============================================================================
// GAME PICKER (founder 2026-07-09 TODO; dropdown rework 2026-07-10) — a
// compact dropdown-style field on the streaming editors (Streams "Your
// Channel" modal + Streaming Hub "Your Stream"). Tapping it opens a
// searchable browser of EVERY game with gameImages/{ABBR}.webp art (the full
// GAME_PROFILES catalog — 20 games — not just the 5 event games), plus an
// "Other" tile with a free-text name.
//
// "Other" is demand tracking: game:'other' + gameCustom:'<name>' stored
// queryably on the stream profile so the founder can see which games
// streamers actually play and pick future event/test-event games.
// ============================================================================

import React, { useState } from 'react';
import {
  View, Text, TouchableOpacity, TextInput, Image, StyleSheet,
  Modal, Pressable, ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { buildStreamGameCatalog, streamGameArtUrl, streamGameName } from '../constants/streamGames';

const GOLD = '#FCD000';

// Full streamable catalog (GAME_PROFILES + browse-only extras with art) —
// CritCash's 5 event games lead, everything else alphabetical.
const CATALOG = buildStreamGameCatalog();

const gameArtUrl = streamGameArtUrl;
const gameName = streamGameName;

const GameArt = ({ gameKey, style }) => {
  const [failed, setFailed] = useState(false);
  const url = gameArtUrl(gameKey);
  if (!url || failed) {
    return (
      <View style={[style, styles.artFallback]}>
        <Ionicons name="game-controller-outline" size={16} color="#777" />
      </View>
    );
  }
  return <Image source={{ uri: url }} style={style} resizeMode="cover" onError={() => setFailed(true)} />;
};

const GameTile = ({ label, gameKey, icon, selected, onPress }) => (
  <TouchableOpacity style={[styles.tile, selected && styles.tileSelected]} onPress={onPress}>
    {gameKey ? (
      <GameArt gameKey={gameKey} style={styles.tileArt} />
    ) : (
      <View style={[styles.tileArt, styles.artFallback]}>
        <Ionicons name={icon || 'sparkles-outline'} size={26} color={selected ? GOLD : '#777'} />
      </View>
    )}
    {selected ? (
      <View style={styles.tileCheck}>
        <Ionicons name="checkmark" size={12} color="#0b0b0b" />
      </View>
    ) : null}
    <Text style={[styles.tileLabel, selected && styles.tileLabelSelected]} numberOfLines={1}>
      {label}
    </Text>
  </TouchableOpacity>
);

/**
 * @param {string} game        selected game key ('' = none, 'other' = custom)
 * @param {string} gameCustom  free-text name when game === 'other'
 * @param {function} onChange  ({ game, gameCustom })
 */
export default function GameTilePicker({ game, gameCustom, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const pick = (key) => {
    onChange({ game: key, gameCustom: key === 'other' ? (gameCustom || '') : '' });
    setOpen(false);
    setSearch('');
  };

  const clear = () => onChange({ game: '', gameCustom: '' });

  const q = search.trim().toLowerCase();
  const filtered = q ? CATALOG.filter((g) => g.name.toLowerCase().includes(q)) : CATALOG;
  const showOther = !q || 'other'.includes(q) || (gameCustom || '').toLowerCase().includes(q);

  const selectedLabel = game === 'other'
    ? (gameCustom ? `Other — ${gameCustom}` : 'Other')
    : game ? gameName(game) : null;

  return (
    <View>
      {/* Collapsed dropdown field */}
      <TouchableOpacity style={styles.field} onPress={() => setOpen(true)}>
        {game && game !== 'other' ? (
          <GameArt gameKey={game} style={styles.fieldArt} />
        ) : (
          <View style={[styles.fieldArt, styles.artFallback]}>
            <Ionicons name={game === 'other' ? 'sparkles-outline' : 'game-controller-outline'} size={14} color="#777" />
          </View>
        )}
        <Text style={[styles.fieldText, !selectedLabel && { color: '#666' }]} numberOfLines={1}>
          {selectedLabel || 'Choose a game…'}
        </Text>
        {selectedLabel ? (
          <TouchableOpacity onPress={clear} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityLabel="Clear game">
            <Ionicons name="close-circle" size={16} color="#666" style={{ marginRight: 8 }} />
          </TouchableOpacity>
        ) : null}
        <Ionicons name="chevron-down" size={16} color="#888" />
      </TouchableOpacity>

      {/* Custom name input rides with 'Other' */}
      {game === 'other' ? (
        <TextInput
          style={styles.customInput}
          value={gameCustom || ''}
          onChangeText={(t) => onChange({ game: 'other', gameCustom: t })}
          placeholder="What game are you playing?"
          placeholderTextColor="#666"
          maxLength={50}
        />
      ) : null}

      {/* Browser modal: search + full art catalog + Other */}
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setOpen(false)}>
          <Pressable style={styles.sheet} onPress={(e) => e.stopPropagation()}>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Choose a game</Text>
              <TouchableOpacity onPress={() => setOpen(false)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Ionicons name="close" size={18} color="#888" />
              </TouchableOpacity>
            </View>
            <TextInput
              style={styles.searchInput}
              value={search}
              onChangeText={setSearch}
              placeholder="Search games…"
              placeholderTextColor="#666"
              autoFocus
            />
            <ScrollView style={styles.sheetScroll} showsVerticalScrollIndicator={false}>
              <View style={styles.grid}>
                {filtered.map((g) => (
                  <GameTile
                    key={g.key}
                    label={g.name}
                    gameKey={g.key}
                    selected={game === g.key}
                    onPress={() => pick(g.key)}
                  />
                ))}
                {showOther ? (
                  <GameTile
                    label="Other"
                    icon="sparkles-outline"
                    selected={game === 'other'}
                    onPress={() => pick('other')}
                  />
                ) : null}
              </View>
              {filtered.length === 0 && !showOther ? (
                <Text style={styles.noResults}>
                  No match — pick "Other" and type the name.
                </Text>
              ) : null}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  // Collapsed field
  field: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, paddingVertical: 8, paddingHorizontal: 10,
  },
  fieldArt: { width: 26, height: 34, borderRadius: 4, marginRight: 10, backgroundColor: '#242424' },
  fieldText: { flex: 1, color: '#EDEDED', fontSize: 13, fontWeight: '600' },
  artFallback: { backgroundColor: '#1c1c1c', alignItems: 'center', justifyContent: 'center' },

  customInput: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, color: '#EDEDED', fontSize: 13,
    paddingVertical: 9, paddingHorizontal: 12, marginTop: 10,
  },

  // Browser modal
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  sheet: {
    width: '100%', maxWidth: 520, maxHeight: '85%',
    backgroundColor: '#111', borderColor: '#222', borderWidth: 1,
    borderRadius: 14, padding: 16,
  },
  sheetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  sheetTitle: { color: '#fff', fontSize: 16, fontWeight: '800' },
  searchInput: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, color: '#EDEDED', fontSize: 13,
    paddingVertical: 8, paddingHorizontal: 12, marginTop: 12,
  },
  sheetScroll: { marginTop: 12 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, paddingBottom: 8 },
  tile: {
    width: 84, borderRadius: 10, overflow: 'hidden',
    backgroundColor: '#161616', borderWidth: 2, borderColor: '#242424',
    alignItems: 'center', paddingBottom: 6,
  },
  tileSelected: { borderColor: GOLD, backgroundColor: '#1a1604' },
  tileArt: { width: '100%', height: 104, backgroundColor: '#242424' },
  tileCheck: {
    position: 'absolute', top: 6, right: 6,
    width: 18, height: 18, borderRadius: 9, backgroundColor: GOLD,
    alignItems: 'center', justifyContent: 'center',
  },
  tileLabel: { color: '#999', fontSize: 10.5, fontWeight: '700', marginTop: 6, paddingHorizontal: 4 },
  tileLabelSelected: { color: GOLD },
  noResults: { color: '#777', fontSize: 12.5, textAlign: 'center', paddingVertical: 20 },
});
